import * as React from 'react'
import { useTranslation } from 'react-i18next'

import * as StoresActions from '../../../actions/stores'
import { EstateUtils, StoreUtils } from '../../../utils'
import { ACL, routes, Routes } from '../../../constants'
import { useAppSelector } from '../../../reducers'
import { useHistory, useLocation, useParams } from 'react-router'
import { useAppDispatch } from '../../../actions/types'
import {
    Centered,
    Colored,
    Column,
    EditableLabel,
    ExternalLink,
    Loader,
    Margin,
} from '../../../components'
import Button, { Sizes as ButtonSizes } from '../../../components/Button'
import Padding, { Sizes as PaddingSizes } from '../../../components/Padding'
import {
    Address,
    AddressHeading,
    AddressSubHeading,
    EmptyData,
    Icon,
    Search,
    StoreInfo as StoreInfoStyle,
} from '../../../components/Screens/Estate/Store/Store.styles'
import useSize from '../../../components/utils/useSize'
import StoreAttributes from '../../../components/Screens/Estate/Store/StoreAttributes'
import { selectStoreWithDevices } from '../../../reducers/stores'
import { StoreWithDevices } from '../../../types/StoreMain'
import type { URLParams } from './DeviceFilters/types'

interface StoreInfoProps {
    locationToSearch: string;
    onChangeLocationToSearch: (location: string) => void;
    openCloseFilters: () => void;
}

function StoreInfo({
    locationToSearch,
    openCloseFilters,
    onChangeLocationToSearch,
}: StoreInfoProps) {
    const dispatch = useAppDispatch()

    const hasPermToEditStore = useAppSelector(
        (state) => EstateUtils.hasPermission(state, { acls: [ACL.STORE_EDIT] }),
    )
    const selectedStore = useAppSelector<StoreWithDevices>(selectStoreWithDevices)
    const isFetchingStoreData = !selectedStore

    const history = useHistory()
    const { search: searchURL } = useLocation()
    const { code, estate, search = '' } = useParams<URLParams>()
    const { isMobile } = useSize()
    const { t } = useTranslation()

    const {
        address,
        attributes = [],
        email,
        name,
        offline,
        phone_primary,
        phone_secondary,
        postcode,
    } = selectedStore ?? {}

    const onChangeSearchId = (id: string) => {
        const url = Routes.buildRoute(routes.ESTATE_STORE, {
            estate,
            code,
            search: id,
        })
        history.replace([url, searchURL].join('?'))
    }

    const onUpdateStoreField = async (field: string, value: string) => {
        const newStore = {
            ...selectedStore,
            [field]: value,
        }
        await dispatch(StoresActions.storeUpdate(newStore, code))
    }

    const renderContactMobile = () => (
        <Padding top size={PaddingSizes.xs}>
            <Column width="calc(100% - 30px)">
                <Padding top size={PaddingSizes.xs}>
                    <EditableLabel blocked={!hasPermToEditStore} value={email} onSubmit={(val) => onUpdateStoreField('email', val)}>
                        <Icon icon="envelope" />
                        &nbsp;&nbsp;
                        {email && <ExternalLink href={`mailto:${email}`}>{email}</ExternalLink>}
                        {!email && <EmptyData>{t('add')}</EmptyData>}
                    </EditableLabel>
                </Padding>
                <Padding top size={PaddingSizes.xs}>
                    <EditableLabel blocked={!hasPermToEditStore} value={phone_primary} onSubmit={(val) => onUpdateStoreField('phone_primary', val)}>
                        {phone_primary && (
                            <ExternalLink href={`"skype:${phone_primary}?call"`}>
                                <Icon icon="phone" />
                                &nbsp;&nbsp;
                                {phone_primary}
                            </ExternalLink>
                        )}
                        {!phone_primary && (
                            <>
                                <Icon icon="phone" />
                                &nbsp;&nbsp;
                                {phone_primary || <EmptyData>{t('add')}</EmptyData>}
                            </>
                        )}
                    </EditableLabel>
                </Padding>
                <Padding top size={PaddingSizes.xs}>
                    <EditableLabel blocked={!hasPermToEditStore} value={phone_secondary} onSubmit={(val) => onUpdateStoreField('phone_secondary', val)}>
                        {phone_secondary && (
                            <ExternalLink href={`"skype:${phone_secondary}?call"`}>
                                &nbsp;&nbsp;&nbsp;&nbsp;
                                {phone_secondary}
                            </ExternalLink>
                        )}
                        {!phone_secondary && (
                            <EmptyData style={{ marginLeft: '21px' }}>{t('add')}</EmptyData>
                        )}
                    </EditableLabel>
                </Padding>
            </Column>
        </Padding>
    )

    const renderContact = () => (
        <Padding noPadding>
            <Column width="30px">
                <Padding top size={PaddingSizes.xs}>
                    <Icon icon="envelope" />
                </Padding>
                <Padding top size={PaddingSizes.xs}>
                    <Icon icon="phone" />
                </Padding>
                <Padding top size={PaddingSizes.xs}>
                    &nbsp;
                </Padding>
            </Column>
            <Column width="calc(100% - 30px)">
                <Padding top size={PaddingSizes.xs}>
                    <EditableLabel blocked={!hasPermToEditStore} value={email} onSubmit={(val) => onUpdateStoreField('email', val)}>
                        {email && <ExternalLink href={`mailto:${email}`}>{email}</ExternalLink>}
                        {!email && <EmptyData>{t('add')}</EmptyData>}
                    </EditableLabel>
                </Padding>
                <Padding top size={PaddingSizes.xs}>
                    <EditableLabel blocked={!hasPermToEditStore} value={phone_primary} onSubmit={(val) => onUpdateStoreField('phone_primary', val)}>
                        {phone_primary && <ExternalLink href={`"skype:${phone_primary}?call"`}>{phone_primary}</ExternalLink>}
                        {!phone_primary && <EmptyData>{t('add')}</EmptyData>}
                    </EditableLabel>
                </Padding>
                <Padding top size={PaddingSizes.xs}>
                    <EditableLabel blocked={!hasPermToEditStore} value={phone_secondary} onSubmit={(val) => onUpdateStoreField('phone_secondary', val)}>
                        {phone_secondary && <ExternalLink href={`"skype:${phone_secondary}?call"`}>{phone_secondary}</ExternalLink>}
                        {!phone_secondary && <EmptyData>{t('add')}</EmptyData>}
                    </EditableLabel>
                </Padding>
            </Column>
        </Padding>
    )

    return (
        <StoreInfoStyle size={PaddingSizes.sm} left top bottom right>
            <Padding className="top-store-" size={PaddingSizes.sm} left={!isMobile} fullHeight noPadding={isMobile}>
                <Column width={isMobile ? '100%' : '48%'} notFullHeight={isMobile}>
                    {isFetchingStoreData && (
                        <Centered horizontal vertical style={{ overflow: 'hidden' }}>
                            <Margin top><Loader /></Margin>
                        </Centered>
                    )}
                    {!isFetchingStoreData && (
                        <>
                            <AddressHeading>
                                <EditableLabel
                                    blocked={!hasPermToEditStore}
                                    onSubmit={(val) => onUpdateStoreField('name', val)}
                                    value={name}
                                >
                                    {name} - {code}
                                </EditableLabel>
                            </AddressHeading>
                            {isMobile && (
                                <div>
                                    <AddressSubHeading>{code}</AddressSubHeading>
                                </div>
                            )}
                            <Address>
                                <AddressSubHeading>
                                    <EditableLabel
                                        blocked={!hasPermToEditStore}
                                        onSubmit={(val) => onUpdateStoreField('address', val)}
                                        value={address}
                                    >
                                        {address}
                                    </EditableLabel>
                                </AddressSubHeading>
                                &#x2c;&nbsp;
                                <AddressSubHeading>
                                    <EditableLabel
                                        blocked={!hasPermToEditStore}
                                        onSubmit={(val) => onUpdateStoreField('postcode', val)}
                                        value={postcode}
                                    >
                                        {postcode}
                                    </EditableLabel>
                                </AddressSubHeading>
                            </Address>
                        </>
                    )}
                </Column>
                <Column width={isMobile ? '100%' : '15%'} notFullHeight={isMobile}>
                    {!isFetchingStoreData && (isMobile ? renderContactMobile() : renderContact())}
                </Column>
                <Column width={isMobile ? '100%' : '5%'} notFullHeight={isMobile}>
                    <StoreAttributes attributes={attributes} />
                </Column>
                <Column width={isMobile ? '100%' : '12.2%'} notFullHeight={true} noOverflow style={{}}>
                    <Centered vertical horizontal>
                        <Colored
                            center
                            color={StoreUtils.offlineColor(typeof offline === 'number' ? 1 - offline : undefined)}
                            style={{
                                position: 'relative',
                                height: '37px',
                                textAlign: 'center',
                                verticalAlign: 'middle',
                                lineHeight: '38px',
                                margin: '5px 11px 5px 5px',
                                width: '100%',
                            }}
                        >
                            {typeof offline === 'number' && <>{StoreUtils.calculateofflinePercentage(offline)}% Online</>}
                            {typeof offline !== 'number' && <>...</>}
                        </Colored>
                    </Centered>
                    <Centered vertical>
                        <Button
                            size={ButtonSizes.xs}
                            icon="sliders-h"
                            padding="6px 12px"
                            fontSize="13px"
                            fullWidth
                            onClick={openCloseFilters}
                            style={{
                                height: '37px',
                                margin: '5px 11px 5px 5px',
                                width: '100%' }}
                        >
                            {t('devices-filter')}
                        </Button>
                    </Centered>
                </Column>
                <Column width={isMobile ? '100%' : '19.8%'} notFullHeight={true} noOverflow style={{}}>
                    <Centered vertical horizontal>
                        <Search
                            id={name}
                            name={name}
                            placeholder={`Search device ID...`}
                            onChange={(e) => onChangeSearchId(e.target.value)}
                            type="text"
                            style={{ margin: '5px 0' }}
                            value={search}
                        />
                    </Centered>
                    <Centered vertical>
                        <Search
                            id={name}
                            name={name}
                            placeholder={`Search location...`}
                            onChange={(e) => onChangeLocationToSearch(e.target.value)}
                            type="text"
                            style={{ margin: '5px 0' }}
                            value={locationToSearch}
                        />
                    </Centered>
                </Column>
            </Padding>
        </StoreInfoStyle>
    )
}

export default StoreInfo
